import { useCallback, useMemo, useState } from "react";
import {
	getCopiedQuestionId,
	getCopiedQuestionsTaskId,
} from "helpers/modelTaskClipboard";
import useRecentlyChangedRows from "./useRecentlyChangedRows";
import useBulkTaskDelete from "./useBulkTaskDelete";
import useBulkTaskDuplicate from "./useBulkTaskDuplicate";
import useBulkPasteQuestions from "./useBulkPasteQuestions";
import useBulkPasteQuestion from "./useBulkPasteQuestion";
import useBulkTaskAssign from "./useBulkTaskAssign";

// Bundles every bulk-action flow (delete, duplicate, paste questions, assign)
// plus the recently-changed tint they share, so ModelTaskTable only needs one
// hook call for the whole "acting on a selection" concern.
const useModelTaskBulkActions = ({ modelId, refreshData, selection, customCaptions }) => {
	const { changedIds, markChanged } = useRecentlyChangedRows();

	const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
	const { deleteTasks, isDeleting } = useBulkTaskDelete({
		modelVersionId: modelId,
		onDeleted: refreshData,
	});
	const confirmBulkDelete = useCallback(async () => {
		const removed = await deleteTasks(selection.selectedRows.map((t) => t.id));
		setBulkDeleteOpen(false);
		if (removed) selection.clear();
	}, [deleteTasks, selection]);

	const { duplicateTasks, isDuplicating } = useBulkTaskDuplicate({
		modelVersionId: modelId,
		onDuplicated: refreshData,
	});
	const confirmBulkDuplicate = useCallback(async () => {
		const created = await duplicateTasks(selection.selectedRows.map((t) => t.id));
		if (created) {
			selection.clear();
			markChanged(created);
		}
	}, [duplicateTasks, markChanged, selection]);

	const { pasteQuestions, isPasting } = useBulkPasteQuestions({
		modelVersionId: modelId,
		onPasted: refreshData,
	});
	const confirmBulkPasteQuestions = useCallback(async () => {
		const pasted = await pasteQuestions(selection.selectedRows.map((t) => t.id));
		if (pasted) markChanged(pasted);
	}, [markChanged, pasteQuestions, selection]);

	const { pasteQuestion, isPasting: isPastingQuestion } = useBulkPasteQuestion({
		modelVersionId: modelId,
		onPasted: refreshData,
	});
	const confirmBulkPasteQuestion = useCallback(async () => {
		const pasted = await pasteQuestion(selection.selectedRows.map((t) => t.id));
		if (pasted) markChanged(pasted);
	}, [markChanged, pasteQuestion, selection]);

	const assign = useBulkTaskAssign({ modelVersionId: modelId, onAssigned: refreshData });
	const assignRelations = useMemo(
		() =>
			assign.RELATIONS.map((relation) => ({
				key: relation.key,
				label: relation.caption(customCaptions),
			})),
		[assign.RELATIONS, customCaptions]
	);
	// The selection survives an assign on purpose: setting Roles then Zones on the
	// same tasks is the common case, and rebuilding the selection between them
	// would be busywork. The tint is what confirms the change landed.
	const applyAssign = useCallback(
		async ({ addIds, removeIds }) => {
			const taskIds = selection.selectedRows.map((t) => t.id);
			const applied = await assign.apply({ taskIds, addIds, removeIds });
			if (applied) markChanged(taskIds);
		},
		[assign, markChanged, selection]
	);

	// Read fresh every render rather than inside the memo below, so a copy
	// made elsewhere on the page (which re-renders this component via the
	// shared context dispatch, but wouldn't otherwise change any of the
	// memo's other inputs) still results in a recomputed list.
	const copiedQuestionsTaskId = getCopiedQuestionsTaskId();
	const copiedQuestionId = getCopiedQuestionId();

	const bulkActions = useMemo(
		() => [
			// Only one of these is ever offered: the clipboard remembers whichever
			// was copied most recently, a single Question or a whole Task's set.
			...(copiedQuestionsTaskId
				? [
						{
							name: `Paste ${customCaptions?.questionPlural || "Questions"}`,
							handler: confirmBulkPasteQuestions,
							disabled: isPasting,
						},
				  ]
				: []),
			...(copiedQuestionId
				? [
						{
							name: `Paste ${customCaptions?.question || "Question"}`,
							handler: confirmBulkPasteQuestion,
							disabled: isPastingQuestion,
						},
				  ]
				: []),
			{ name: "Duplicate", handler: confirmBulkDuplicate, disabled: isDuplicating },
			{ name: "Remove", handler: () => setBulkDeleteOpen(true), isDestructive: true },
		],
		[
			confirmBulkDuplicate,
			confirmBulkPasteQuestion,
			confirmBulkPasteQuestions,
			copiedQuestionId,
			copiedQuestionsTaskId,
			customCaptions,
			isDuplicating,
			isPasting,
			isPastingQuestion,
		]
	);

	return {
		changedIds,
		bulkDeleteOpen,
		openBulkDelete: () => setBulkDeleteOpen(true),
		closeBulkDelete: () => setBulkDeleteOpen(false),
		confirmBulkDelete,
		isDeleting,
		confirmBulkDuplicate,
		isDuplicating,
		confirmBulkPasteQuestions,
		isPasting,
		confirmBulkPasteQuestion,
		isPastingQuestion,
		assign,
		assignRelations,
		applyAssign,
		bulkActions,
	};
};

export default useModelTaskBulkActions;
